import React from 'react';
import {
  Box, TextField, Typography, ToggleButton, ToggleButtonGroup, MenuItem, IconButton, Tooltip, Paper
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  ReglePiece, FormatSortie, Proportions, FORMATS_SORTIE, estSortieImage, nettoyerRegle, resumerRegle, validerRegle, formaterKo
} from '../services/normesPjServices';

// Éditeur d'une règle de pièce (menu « Pièces jointes ») : formats acceptés, format de conversion, poids,
// et pour une conversion en image : dimensions et proportions. Les valeurs de dimensions restent en mémoire
// quand on passe en PDF (retour possible) ; nettoyerRegle les vide à l'enregistrement.

interface Props {
  regle: ReglePiece;
  onChange: (r: ReglePiece) => void;
  titre: string;
  aide?: string;
  onSupprimer?: () => void;
}

// '' -> null ; sinon nombre (NaN refusé par validerRegle)
const nombre = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const aideKo = (ko: number | null): string => (ko !== null && Number.isInteger(ko) && ko >= 1024 ? `= ${formaterKo(ko)}` : 'Ko');

const ReglePieceEditor: React.FC<Props> = ({ regle, onChange, titre, aide, onSupprimer }) => {
  const maj = (champs: Partial<ReglePiece>) => onChange({ ...regle, ...champs });
  const image = estSortieImage(regle);
  const erreurs = validerRegle(regle);
  const resume = resumerRegle(nettoyerRegle(regle)); // ce qui sera réellement enregistré

  const champNombre = (label: string, valeur: number | null, cle: keyof ReglePiece, suffixe: string) => (
    <TextField
      type="number"
      label={label}
      size="small"
      sx={{ width: 140 }}
      inputProps={{ min: 1 }}
      value={valeur === null || Number.isNaN(valeur) ? '' : valeur}
      onChange={(e) => maj({ [cle]: nombre(e.target.value) } as Partial<ReglePiece>)}
      helperText={suffixe}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2">{titre}</Typography>
          {aide && <Typography variant="caption" color="text.secondary" display="block">{aide}</Typography>}
        </Box>
        {onSupprimer && (
          <Tooltip title="Retirer cette règle">
            <IconButton size="small" color="error" onClick={onSupprimer}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1.5 }}>
        <TextField
          label="Libellé"
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          value={regle.libelle}
          onChange={(e) => maj({ libelle: e.target.value })}
          helperText="Sert au nom du fichier téléchargé (NOM_Prénom_Libellé)"
        />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Formats acceptés</Typography>
          <ToggleButtonGroup
            size="small"
            value={regle.formats}
            onChange={(_, v: FormatSortie[]) => {
              if (!v || !v.length) return; // au moins un format
              const formats = FORMATS_SORTIE.filter((f) => v.includes(f));
              maj({ formats, format_sortie: formats.includes(regle.format_sortie) ? regle.format_sortie : formats[0] });
            }}
          >
            {FORMATS_SORTIE.map((f) => (
              <ToggleButton key={f} value={f} sx={{ px: 2 }}>{f.toUpperCase()}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <TextField
          select
          label="Convertir en"
          size="small"
          sx={{ width: 140 }}
          value={regle.format_sortie}
          disabled={regle.formats.length < 2}
          onChange={(e) => maj({ format_sortie: e.target.value as FormatSortie })}
          helperText={regle.formats.length < 2 ? 'Seul format accepté' : 'Format produit'}
        >
          {regle.formats.map((f) => (
            <MenuItem key={f} value={f}>{f.toUpperCase()}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        {champNombre('Poids min', regle.taille_min_ko, 'taille_min_ko', aideKo(regle.taille_min_ko))}
        {champNombre('Poids max', regle.taille_max_ko, 'taille_max_ko', aideKo(regle.taille_max_ko))}
      </Box>

      {image ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            {champNombre('Largeur min', regle.largeur_min, 'largeur_min', 'px')}
            {champNombre('Largeur max', regle.largeur_max, 'largeur_max', 'px')}
            {champNombre('Hauteur min', regle.hauteur_min, 'hauteur_min', 'px')}
            {champNombre('Hauteur max', regle.hauteur_max, 'hauteur_max', 'px')}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
            <TextField
              select
              label="Proportions"
              size="small"
              sx={{ width: 200 }}
              value={regle.proportions}
              onChange={(e) => maj({ proportions: e.target.value as Proportions })}
              helperText={regle.proportions === 'libres' ? ' ' : 'Recadrage à la conversion'}
            >
              <MenuItem value="libres">Libres</MenuItem>
              <MenuItem value="carree">Carrée</MenuItem>
              <MenuItem value="personnalisees">Personnalisées (l : h)</MenuItem>
            </TextField>
            {regle.proportions === 'personnalisees' && (
              <>
                {champNombre('Largeur (l)', regle.ratio_l, 'ratio_l', 'ex. 35')}
                {champNombre('Hauteur (h)', regle.ratio_h, 'ratio_h', 'ex. 45')}
              </>
            )}
          </Box>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Conversion en PDF : dimensions et proportions ne s'appliquent pas (une image devient une page ; un PDF trop
          lourd est recompressé).
        </Typography>
      )}

      {erreurs.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {erreurs.map((e) => (
            <Typography key={e} variant="caption" color="error" display="block">{e}</Typography>
          ))}
        </Box>
      )}
      <Typography variant="body2" color="text.secondary">
        Résumé : <strong>{resume || 'aucune contrainte'}</strong>
      </Typography>
    </Paper>
  );
};

export default ReglePieceEditor;
